import React, { useEffect, useState } from "react"
import moment from "moment"
import { useFormik } from "formik"
import toast from "react-hot-toast"
import { Col, Form, Row } from "reactstrap"
import ReactDatePicker from "react-datepicker"

import { CloneProductionLine } from "api/api.service"
import { Button } from "components/Button"
import FormikErrorText from "components/FormikErrorText/FormikErrorText"
import { Modal } from "components/Modal"
import { TextInput } from "components/TextInput"
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils"
import {
   FIELDS_NAME,
   initialValues,
   getValidationSchema,
   TOMORROW_DATE,
} from "../FormConfig"

import calendarIcon from "assets/images/icons/calendar.svg"

const TITLES = {
   MODAL_TITLE: "Duplicate Production Line",
   SUBMIT_BTN_TEXT: "Duplicate Production Line",
}

/**
 * Creates a new production line from an existing one. The stages, stations, moulds and weekday
 * slot configuration are copied server side, so this form only collects what cannot be shared
 * between two lines: a unique name, and the schedule position (starting slot and dates).
 *
 * Date overrides are deliberately not carried over — they are tied to specific date ranges that
 * will not line up with a line starting on a different date.
 */
const DuplicateProductionLine = ({
   isOpen = false,
   onToggleModal = () => {},
   onSuccess = () => {},
   sourceLine = null,
}) => {
   const [isLoading, setIsLoading] = useState(false)

   const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
         ...initialValues,
      },
      validationSchema: getValidationSchema(),
      onSubmit: values => {
         handleSubmit(values)
      },
   })

   useEffect(() => {
      if (!sourceLine) return

      formik.setValues({
         ...initialValues,
         [FIELDS_NAME.NAME]: `${sourceLine?.name || ""} (Copy)`,
      })
   }, [sourceLine?.id])

   const handleSubmit = async values => {
      const bodyData = {
         name: values[FIELDS_NAME.NAME],
         startDate: convertDateTime({
            date: moment(values[FIELDS_NAME.START_DATE]).utc().startOf("day"),
            customFormat: dateTimeFormat.apiDateTimeFormat,
         }),
         endDate: values[FIELDS_NAME.END_DATE]
            ? convertDateTime({
                 date: moment(values[FIELDS_NAME.END_DATE]).utc().endOf("day"),
                 customFormat: dateTimeFormat.apiDateTimeFormat,
              })
            : null,
         startingSlotId: Number(values[FIELDS_NAME.SLOT_SEQUENCE_NUMBER]),
      }

      try {
         setIsLoading(true)
         await CloneProductionLine(sourceLine?.id, bodyData)
         toast.success("Production Line has been duplicated successfully !")
         onSuccess()
         handleModalClose()
      } catch (error) {
      } finally {
         setIsLoading(false)
      }
   }

   const handleModalClose = () => {
      formik.resetForm()
      onToggleModal()
   }

   return (
      <Modal
         isOpen={isOpen}
         handleModalToggling={handleModalClose}
         customButton={true}
         hideModalHeaderSeparator={true}
         headerClasses="header-container justify-content-center mb-0"
         sizeClasses="2"
         backdrop="static"
         modalTitle={TITLES.MODAL_TITLE}
      >
         <div style={{ pointerEvents: isLoading ? "none" : "auto" }}>
            <Row>
               <Col xs={12}>
                  <p className="text-muted mb-4">
                     Stages, stations, mould colors and daily slot configuration
                     will be copied from{" "}
                     <strong>{sourceLine?.name || "the selected line"}</strong>.
                     The new line starts inactive so it does not take scheduling
                     work until you switch it on. Date overrides are not copied.
                  </p>

                  <Form onSubmit={formik.handleSubmit}>
                     <Row className="mb-3 align-items-center">
                        <label className=" col-md-3 col-form-label clr-theme-primary">
                           Name
                        </label>
                        <div className="col-md-9">
                           <TextInput
                              placeholder="Enter Name"
                              name={FIELDS_NAME.NAME}
                              {...formik.getFieldProps(FIELDS_NAME.NAME)}
                           />
                           <FormikErrorText
                              formikInstance={formik}
                              fieldName={FIELDS_NAME.NAME}
                           />
                        </div>
                     </Row>

                     <Row className="mt-4 mb-3 align-items-center">
                        <label className=" col-md-3 col-form-label clr-theme-primary">
                           Start Date
                        </label>
                        <div className="col-md-9">
                           <div className="position-relative">
                              <ReactDatePicker
                                 autoComplete="off"
                                 dateFormat="MM/dd/yyyy"
                                 showTimeInput={false}
                                 calendarIcon={calendarIcon}
                                 selected={
                                    formik.values[FIELDS_NAME.START_DATE]
                                       ? new Date(
                                            formik.values[
                                               FIELDS_NAME.START_DATE
                                            ]
                                         )
                                       : ""
                                 }
                                 placeholderText="Select Start Date"
                                 minDate={TOMORROW_DATE}
                                 name={FIELDS_NAME.START_DATE}
                                 onChange={date =>
                                    formik.setFieldValue(
                                       FIELDS_NAME.START_DATE,
                                       date
                                    )
                                 }
                                 className="custom-date-picker"
                              />
                              <img
                                 src={calendarIcon}
                                 alt="Calendar"
                                 className="calendar-icon"
                              />
                           </div>
                           <FormikErrorText
                              formikInstance={formik}
                              fieldName={FIELDS_NAME.START_DATE}
                           />
                        </div>
                     </Row>

                     <Row className="mt-4 mb-3 align-items-center">
                        <label className=" col-md-3 col-form-label clr-theme-primary">
                           End Date
                        </label>
                        <div className="col-md-9 position-relative">
                           <div className="position-relative">
                              <ReactDatePicker
                                 autoComplete="off"
                                 dateFormat="MM/dd/yyyy"
                                 showTimeInput={false}
                                 calendarIcon={calendarIcon}
                                 selected={
                                    formik.values[FIELDS_NAME.END_DATE]
                                       ? new Date(
                                            formik.values[FIELDS_NAME.END_DATE]
                                         )
                                       : ""
                                 }
                                 isClearable={Boolean(
                                    formik.values[FIELDS_NAME.END_DATE]
                                 )}
                                 placeholderText="Select End Date"
                                 minDate={new Date()}
                                 name={FIELDS_NAME.END_DATE}
                                 onChange={date =>
                                    formik.setFieldValue(
                                       FIELDS_NAME.END_DATE,
                                       date
                                    )
                                 }
                                 className="custom-date-picker"
                              />
                              <img
                                 src={calendarIcon}
                                 alt="Calendar"
                                 className="calendar-icon"
                              />
                           </div>
                           <FormikErrorText
                              formikInstance={formik}
                              fieldName={FIELDS_NAME.END_DATE}
                           />
                        </div>
                     </Row>

                     <Row className="mb-3 align-items-center">
                        <label className=" col-md-3 col-form-label clr-theme-primary">
                           Slot Sequence Number
                        </label>
                        <div className="col-md-9">
                           <TextInput
                              placeholder="Enter Slot Sequence Number"
                              type="number"
                              name={FIELDS_NAME.SLOT_SEQUENCE_NUMBER}
                              {...formik.getFieldProps(
                                 FIELDS_NAME.SLOT_SEQUENCE_NUMBER
                              )}
                           />
                           <FormikErrorText
                              formikInstance={formik}
                              fieldName={FIELDS_NAME.SLOT_SEQUENCE_NUMBER}
                           />
                        </div>
                     </Row>

                     <div className="d-flex justify-content-center mt-3 mt-2">
                        <Button
                           isLoading={isLoading}
                           type={"submit"}
                           title={TITLES.SUBMIT_BTN_TEXT}
                           className="header-button"
                        />
                     </div>
                  </Form>
               </Col>
            </Row>
         </div>
      </Modal>
   )
}

export default DuplicateProductionLine
